import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Mail, Paperclip, Save, Send, Smartphone, Users, CheckCircle2, AlertTriangle, Clock, CornerDownRight, Plus } from "lucide-react";
import { api } from "./api";
import { Badge, Cellule, EnTeteMenu, Modale, Puces, Recherche, Section, Tableau } from "./ui";
import { useConfirmation } from "./Confirmation";
import { useChantiers } from "../lib/useChantiers";

/* Écrire à tout un groupe : les inscrits d'un cours, d'un stage, d'un spectacle, d'un
   workshop — ou toutes les familles de la saison, ou une seule famille. Un seul e-mail par
   FAMILLE, même avec deux enfants dans le stage, et le message est déposé dans l'espace
   famille.

   L'écran se lit de haut en bas, en trois étapes numérotées (19.10.2026) : 1. à qui (le
   type, puis — en retrait, juste en dessous — quel cours ou quel stage), 2. qui exactement
   (tout le groupe coché, « Tout décocher » pour n'en garder qu'une), 3. le message. */
const TYPES = [
  { valeur: "cours", label: "Un cours", quel: "Quel cours ?" },
  { valeur: "stages", label: "Un stage", quel: "Quel stage ?" },
  { valeur: "auditions", label: "Une audition", quel: "Quelle audition ?" },
  { valeur: "spectacles", label: "Un spectacle", quel: "Quel spectacle ?" },
  { valeur: "workshops", label: "Un workshop heels", quel: "Quelle date de workshop ?" },
  // Les bénévoles des spectacles de juin (01.11.2026 : « Écrire à tous les bénévoles » de
  // Spectacles › Bénévoles arrivait ici sans sa puce) : seulement là où le chantier est ouvert.
  { valeur: "benevoles", label: "Bénévoles des spectacles", quel: "Quels bénévoles ?", chantier: "spectacles_juin" },
  { valeur: "familles", label: "Toutes les familles de la saison" },
  { valeur: "choix", label: "Une ou quelques familles" },
];
const SANS_GROUPE = ["familles", "choix"];

/* Un cours se reconnaît comme partout dans la console : jour et heure, nom (discipline et
   âge), salle, prof. Deux « Comédie musicale 7-9 ans » le lundi à 17h ne se distinguent
   que par la salle et le prof. */
const libelleGroupe = (type, g) => {
  const nb = g.nb_inscrits == null ? "" : ` — ${g.nb_inscrits} ${type === "cours" ? "élève" : "inscrit"}${g.nb_inscrits > 1 ? "s" : ""}`;
  if (type === "cours") {
    return [`${g.jour || ""} ${g.debut || ""}${g.fin ? `–${g.fin}` : ""}`.trim(), g.nom, g.salle, g.professeur]
      .filter(Boolean).join(" · ") + nb;
  }
  const quand = g.quand && !String(g.nom || "").includes(g.quand) ? g.quand : "";
  return [g.nom, quand].filter(Boolean).join(" · ") + nb;
};

const Etape = ({ n, children }) => (
  <span className="inline-flex items-center gap-2.5">
    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold"
      style={{ background: "var(--primary)", color: "#04202A" }}>{n}</span>
    {children}
  </span>
);
export default function Envois({ cible }) {
  const { demander, informer } = useConfirmation();
  const chantiers = useChantiers("console");
  // « stages::Stage d'automne — … » : on coupe à la PREMIÈRE séparation, le nom du groupe
  // peut contenir n'importe quoi.
  // « …##modele=cle » : le modèle à poser d'office (Spectacles › Noël → « Écrire aux familles »).
  const [cibleSeule, modeleDemande] = String(cible || "").split("##modele=");
  const coupe = cibleSeule.indexOf("::");
  // Application → Qui a l'app → « Pas de compte » → « Ouvrir les envois groupés » : le mode
  // d'emploi de l'app, prêt pour les familles de la saison qui ne l'ont pas encore.
  const invitation = cible === "invitation-app";
  const [type, setType] = useState(() => (invitation ? "familles" : coupe > 0 ? cibleSeule.slice(0, coupe) : "stages"));
  const [groupeId, setGroupeId] = useState(() => (coupe > 0 ? cibleSeule.slice(coupe + 2) : ""));
  // Spectacle de Noël : sous le message, le bouton personnel « Modifier ou annuler ma
  // réservation » de chaque famille ; seules les familles avec une place confirmée le
  // reçoivent (Jen, 03.11.2026).
  const [lien, setLien] = useState(false);
  const [groupes, setGroupes] = useState([]);
  const [dest, setDest] = useState(null);
  // Les adresses cochées : tout le groupe au départ, personne pour « une ou quelques familles ».
  const [choisis, setChoisis] = useState([]);
  const [attente, setAttente] = useState(false);
  const [trouve, setTrouve] = useState("");
  const [piece, setPiece] = useState("");
  const [q, setQ] = useState("");
  const [sujet, setSujet] = useState("");
  const [message, setMessage] = useState("");
  const [modeles, setModeles] = useState([]);
  // « Visible dans l'app » : le message est aussi déposé dans l'app des familles (Accueil
  // tant qu'il n'est pas lu, puis Profil → Messages de l'école). Chaque modèle garde son
  // réglage ; le mode d'emploi de l'app n'y va pas (école, 20.10.2026).
  const [espace, setEspace] = useState(true);
  const [sansApp, setSansApp] = useState(invitation);
  const invitationPrete = useRef(false);
  const [histo, setHisto] = useState([]);
  const [envoi, setEnvoi] = useState(false);
  // Un envoi passé ouvert depuis « Derniers envois » : le message tel qu'il est parti.
  const [vu, setVu] = useState(null);
  const fichier = useRef(null);
  // Clé d'idempotence du message en cours de rédaction : un double clic ou un nouvel
  // essai après une coupure ne renvoie jamais le même message deux fois.
  const cleEnvoi = useRef(`${Date.now()}-${Math.random().toString(36).slice(2)}`);

  const chargerModeles = useCallback(() => {
    api("/envois/modeles").then((r) => setModeles(r.items || [])).catch(() => {});
    api("/envois/historique").then((r) => setHisto(r.items || [])).catch(() => {});
  }, []);
  useEffect(() => { chargerModeles(); }, [chargerModeles]);

  // Les groupes disponibles viennent de l'onglet Liste : une seule source de vérité. Aucun
  // n'est pris d'office : on choisit le cours soi-même (le premier de la liste, pris par
  // défaut, pouvait recevoir un message qui ne lui était pas destiné).
  useEffect(() => {
    if (SANS_GROUPE.includes(type)) { setGroupes([]); setGroupeId(""); return; }
    // Les bénévoles des spectacles n'ont pas d'onglet Liste : leurs groupes viennent des envois.
    api(type === "benevoles" ? "/envois/groupes/benevoles" : `/liste/${type}`).then((r) => setGroupes(r.items || [])).catch(() => setGroupes([]));
  }, [type]);

  useEffect(() => {
    if (!SANS_GROUPE.includes(type) && !groupeId) { setDest(null); setChoisis([]); return; }
    setDest(null);
    api(`/envois/destinataires?type=${type}&id=${encodeURIComponent(groupeId)}${attente ? "&attente=true" : ""}${lien ? "&lien=true" : ""}`)
      .then((r) => {
        setDest(r);
        setChoisis(type === "choix" ? [] : (r.items || []).filter((d) => !sansApp || !d.app).map((d) => d.email));
      })
      .catch(() => { setDest({ items: [] }); setChoisis([]); });
  }, [type, groupeId, attente, lien]); // eslint-disable-line react-hooks/exhaustive-deps

  const modele = (m) => {
    setSujet(m.sujet || m.nom); setMessage(m.message || "");
    setEspace(m.espace !== false);
    if (m.sans_app) basculerSansApp(true);
    if (m.lien_spectacle) {
      if (type !== "spectacles") { setType("spectacles"); setGroupeId(""); }
      setLien(true);
    }
  };
  // Arrivé depuis Spectacles › Noël : le modèle se pose dès que les modèles sont là.
  const modelePose = useRef(false);
  useEffect(() => {
    if (!modeleDemande || modelePose.current) return;
    const m = modeles.find((x) => x.cle === modeleDemande);
    if (m) { modelePose.current = true; modele(m); }
  }, [modeles]); // eslint-disable-line react-hooks/exhaustive-deps
  // L'invitation : le modèle « mode d'emploi » se pose dès que les modèles sont là.
  useEffect(() => {
    if (!invitation || invitationPrete.current) return;
    const m = modeles.find((x) => x.cle === "application-mode-emploi");
    if (m) { invitationPrete.current = true; modele(m); }
  }, [modeles]); // eslint-disable-line react-hooks/exhaustive-deps

  /* « Seulement les familles qui n'ont pas encore l'app » : celles qui l'ont sont décochées
     (et recochées si on enlève la case). */
  function basculerSansApp(oui) {
    setSansApp(oui);
    if (enChoix) return;
    const avec = new Set((dest?.items || []).filter((d) => d.app).map((d) => d.email));
    setChoisis((x) => (oui ? x.filter((e) => !avec.has(e))
      : [...new Set([...x, ...(dest?.items || []).map((d) => d.email)])]));
  }

  const enChoix = type === "choix";
  const items = useMemo(() => {
    const cle = q.trim().toLowerCase();
    return (dest?.items || [])
      .filter((d) => !enChoix || choisis.includes(d.email))
      .filter((d) => !cle
        || `${d.famille} ${d.email} ${(d.inscrits || []).join(" ")}`.toLowerCase().includes(cle));
  }, [dest, q, enChoix, choisis]);
  // « Une ou quelques familles » : on cherche une famille par le parent, un enfant ou
  // l'adresse, et on l'ajoute d'un clic.
  const trouvees = useMemo(() => {
    const cle = trouve.trim().toLowerCase();
    if (!enChoix || cle.length < 2) return [];
    return (dest?.items || []).filter((d) => !choisis.includes(d.email)
      && `${d.famille} ${d.email} ${(d.inscrits || []).join(" ")}`.toLowerCase().includes(cle)).slice(0, 8);
  }, [dest, trouve, enChoix, choisis]);
  const retenus = (dest?.items || []).filter((d) => choisis.includes(d.email));
  const avecLien = lien && !!dest?.lien_possible;
  const groupe = groupes.find((g) => g.id === groupeId);
  const nomGroupe = type === "familles" ? "Toutes les familles de la saison"
    : enChoix ? (retenus.length === 1 ? `Famille ${retenus[0].famille || retenus[0].email}`
      : `${retenus.length} familles choisies`)
    : (groupe ? libelleGroupe(type, groupe).split(" — ")[0] : groupeId);
  const cocher = (email, oui) => setChoisis((x) => (oui ? [...x.filter((v) => v !== email), email] : x.filter((v) => v !== email)));
  const parJour = type === "cours"
    ? groupes.reduce((acc, g) => { const j = g.jour || "Autre"; acc[j] = [...(acc[j] || []), g]; return acc; }, {})
    : null;
  const famillesMot = (n) => `${n} famille${n > 1 ? "s" : ""}`;
  // Les types d'un chantier fermé n'apparaissent pas (sauf celui déjà choisi).
  const types = TYPES.filter((t) => !t.chantier || chantiers[t.chantier] || t.valeur === type);

  const envoyer = async () => {
    if (!await demander({
      titre: `Écrire à ${famillesMot(retenus.length)}`,
      texte: `Objet : « ${sujet} »`,
      points: [`Groupe : ${nomGroupe}`,
        fichier.current?.files?.[0] ? `Pièce jointe : ${fichier.current.files[0].name}` : "Sans pièce jointe",
        espace ? "Visible aussi dans l'app des familles (Accueil tant qu'il n'est pas lu, puis Profil → Messages)."
          : "Pas dans l'app : e-mail seulement.",
        ...(avecLien ? ["Sous le message, chaque famille reçoit son bouton personnel « Modifier ou annuler ma réservation »."] : [])],
      valider: "Envoyer",
    })) return;
    setEnvoi(true);
    try {
      const form = new FormData();
      form.append("corps", JSON.stringify({
        type, id: groupeId, groupe: nomGroupe, sujet, message, espace, attente, lien_spectacle: avecLien,
        emails: retenus.map((d) => d.email), cle: cleEnvoi.current,
      }));
      if (fichier.current?.files?.[0]) form.append("fichier", fichier.current.files[0]);
      const r = await api("/envois", { method: "POST", body: form });
      await informer({
        titre: r.deja_envoye ? "Déjà envoyé" : (r.simule ? "Envoi simulé (mode test)" : "Envoi lancé"),
        ton: "succes",
        texte: `${r.a_envoyer ?? r.envoyes} famille(s) · ${nomGroupe}`,
        points: [
          ...(r.deja_envoye ? ["Ce message est déjà parti (ou part en ce moment) : rien n'est renvoyé."] : []),
          ...(!r.deja_envoye ? ["Les e-mails partent en arrière-plan : « Derniers envois » montre l'avancement."] : []),
          ...(r.simule ? ["La console est en mode test : rien n'est parti aux familles."] : []),
        ],
      });
      cleEnvoi.current = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setSujet(""); setMessage("");
      if (fichier.current) fichier.current.value = "";
      setPiece("");
      chargerModeles();
    } catch (e) {
      await informer({ titre: "Envoi impossible", ton: "danger", texte: String(e.message || e) });
    }
    setEnvoi(false);
  };

  const enregistrerModele = async () => {
    const nom = sujet.trim();
    if (!nom) return;
    const cle = nom.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40);
    await api(`/envois/modeles/${encodeURIComponent(cle)}`,
      { method: "PUT", body: JSON.stringify({ nom, sujet, message, espace, lien_spectacle: avecLien }) });
    chargerModeles();
    await informer({ titre: "Modèle enregistré", ton: "succes", texte: nom });
  };

  return (
    <div className="space-y-6" data-testid="envois">
      <EnTeteMenu ton="messages" Icon={Send} contexte="Écrire aux familles" titre="Envois groupés" testid="envois-entete"
        points={[
          [Users, "À qui", "Un cours, un stage, un spectacle… ou une seule famille"],
          [Mail, "Un e-mail par famille", "Même avec deux enfants dans le groupe"],
          [Smartphone, "Aussi dans l'app", "Le message reste dans l'espace famille"],
        ]} />

      <Section titre={<Etape n={1}>À qui ?</Etape>} testid="envois-groupe">
        <div className="bg-white rounded-2xl border p-5 space-y-4" style={{ borderColor: "var(--border)" }}>
          <div>
            <div className="text-xs font-semibold text-[var(--muted)] mb-2">Écrire à…</div>
            <Puces testid="envois-types" valeur={type} options={types}
              onChange={(v) => { setType(v); setGroupeId(""); setAttente(false); setQ(""); setTrouve(""); if (v !== "spectacles") setLien(false); }} />
          </div>

          {/* Le deuxième filtre, en retrait SOUS le premier : il dépend de lui. */}
          {!SANS_GROUPE.includes(type) && (
            <div className="ml-2 pl-4 border-l-2 space-y-3" style={{ borderColor: "var(--primary)" }}
              data-testid="envois-sous-filtre">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-[var(--muted)]">
                <CornerDownRight size={13} /> {types.find((t) => t.valeur === type)?.quel}
              </label>
              <select className="input-field w-full max-w-3xl" value={groupeId} data-testid="envois-groupe-select"
                onChange={(e) => { setGroupeId(e.target.value); setAttente(false); }}>
                <option value="">{groupes.length ? "— Choisis dans la liste —" : "Chargement…"}</option>
                {parJour
                  ? Object.entries(parJour).map(([jour, liste]) => (
                    <optgroup key={jour} label={jour}>
                      {liste.map((g) => <option key={g.id} value={g.id}>{libelleGroupe(type, g)}</option>)}
                    </optgroup>
                  ))
                  : groupes.map((g) => <option key={g.id} value={g.id}>{libelleGroupe(type, g)}</option>)}
              </select>
              {dest?.lien_possible && (
                <div className="flex flex-wrap items-center gap-3 rounded-xl px-4 py-3 max-w-3xl" data-testid="envois-lien"
                  style={{ background: lien ? "#EBFBFC" : "#F8FAFC", border: `1px solid ${lien ? "#BFEFF2" : "var(--border)"}` }}>
                  <div className="flex-1 min-w-[14rem]">
                    <div className="text-sm font-semibold">Ajouter le lien « Modifier ou annuler ma réservation »</div>
                    <div className="text-xs text-[var(--muted)] mt-0.5">
                      {lien ? "Chaque famille reçoit son bouton personnel sous le message. Seules les familles avec une place confirmée le reçoivent (pas la liste d'attente)."
                        : "Sous le message, le bouton personnel de chaque famille pour changer le nombre de places ou annuler."}
                    </div>
                  </div>
                  <Puces valeur={lien ? "oui" : "non"} onChange={(v) => setLien(v === "oui")} testid="envois-lien-choix"
                    options={[{ valeur: "oui", label: "Oui", testid: "envois-lien-oui" }, { valeur: "non", label: "Non", testid: "envois-lien-non" }]} />
                </div>
              )}
              {type === "cours" && groupe?.nb_attente > 0 && (
                <label className="flex items-center gap-2 text-sm" data-testid="envois-attente">
                  <input type="checkbox" checked={attente} onChange={(e) => setAttente(e.target.checked)} />
                  Prévenir aussi la liste d'attente ({groupe.nb_attente})
                </label>
              )}
            </div>
          )}

          {type === "familles" && (
            <p className="ml-2 pl-4 border-l-2 text-sm text-[var(--muted)]" style={{ borderColor: "var(--primary)" }}>
              Chaque famille qui a au moins un cours actif cette saison, une seule fois.
            </p>
          )}

          {enChoix && (
            <div className="ml-2 pl-4 border-l-2 space-y-2" style={{ borderColor: "var(--primary)" }}
              data-testid="envois-choix">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-[var(--muted)]">
                <CornerDownRight size={13} /> Cherche une famille, puis ajoute-la
              </label>
              <Recherche valeur={trouve} onChange={setTrouve} largeur={420} testid="envois-choix-recherche"
                placeholder={dest ? "Nom du parent, prénom d'un enfant ou adresse…" : "Chargement des familles…"} />
              {trouve.trim().length >= 2 && (
                <div className="rounded-xl border divide-y max-w-3xl bg-white" style={{ borderColor: "var(--border)" }}>
                  {trouvees.length === 0 && <p className="px-3 py-2 text-sm text-[var(--muted)]">Aucune famille trouvée.</p>}
                  {trouvees.map((d) => (
                    <button key={d.email} type="button" data-testid="envois-choix-ajouter"
                      className="w-full flex flex-wrap items-center gap-x-3 gap-y-0.5 px-3 py-2 text-left text-sm hover:bg-[#F8FAFC]"
                      style={{ borderColor: "var(--border-soft)" }}
                      onClick={() => { cocher(d.email, true); setTrouve(""); }}>
                      <span className="font-semibold">{d.famille || d.email}</span>
                      {(d.inscrits || []).length > 0 && <span className="text-[var(--muted)]">{d.inscrits.join(", ")}</span>}
                      <span className="text-xs text-[var(--muted)] break-all">{d.email}</span>
                      <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold" style={{ color: "var(--primary)" }}>
                        <Plus size={13} /> Ajouter
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </Section>

      <Section titre={<Etape n={2}>Qui reçoit le message</Etape>}
        aide={enChoix
          ? "Les familles ajoutées à l'étape 1. Décoche pour en retirer une."
          : "Tout le groupe est coché. Décoche qui ne doit pas le recevoir — ou « Tout décocher » puis coche une seule personne."}
        action={dest && (
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-full"
            style={{ background: retenus.length ? "#ECFEFF" : "#F1F5F9", color: retenus.length ? "#0E7490" : "var(--muted)" }}
            data-testid="envois-compteur">
            <Users size={14} />
            {enChoix ? `${famillesMot(retenus.length)} choisie${retenus.length > 1 ? "s" : ""}`
              : `${retenus.length} sur ${famillesMot(dest.items.length)}`}
          </span>
        )}
        testid="envois-destinataires">
        {!dest ? (
          <p className="text-sm text-[var(--muted)]">
            {!SANS_GROUPE.includes(type) && !groupeId ? "Choisis d'abord le groupe à l'étape 1." : "Chargement…"}
          </p>
        ) : (
          <>
            {(!enChoix || retenus.length > 0) && (
              <div className="flex flex-wrap items-center gap-2">
                <Recherche valeur={q} onChange={setQ} largeur={280} testid="envois-recherche"
                  placeholder="Chercher dans la liste…" />
                {!enChoix && dest.items.some((d) => d.app) && (
                  <label className="inline-flex items-center gap-2 text-sm cursor-pointer" data-testid="envois-sans-app">
                    <input type="checkbox" checked={sansApp} onChange={(e) => basculerSansApp(e.target.checked)} />
                    Seulement les familles qui n'ont pas encore l'app
                    <span className="text-xs text-[var(--muted)]">({dest.items.filter((d) => d.app).length} l'ont déjà)</span>
                  </label>
                )}
                {!enChoix && (
                  <>
                    <button className="btn-ghost text-sm" data-testid="envois-tout-cocher"
                      disabled={retenus.length === dest.items.length}
                      onClick={() => setChoisis(dest.items.map((d) => d.email))}>Tout cocher</button>
                    <button className="btn-ghost text-sm" data-testid="envois-tout-decocher"
                      disabled={retenus.length === 0} onClick={() => setChoisis([])}>Tout décocher</button>
                  </>
                )}
              </div>
            )}
            <Tableau
              testid="tableau-envois-destinataires"
              entetes={["", "Famille", type === "familles" || enChoix ? "Enfant(s)" : "Inscrit(s)", "E-mail"]}
              lignes={items}
              vide={enChoix ? "Personne pour l'instant : cherche une famille à l'étape 1." : "Personne dans ce groupe."}
              rendu={(d) => (
                <>
                  <Cellule>
                    <input type="checkbox" data-testid="envois-case" aria-label={`Envoyer à ${d.famille || d.email}`}
                      checked={choisis.includes(d.email)}
                      onChange={(e) => cocher(d.email, e.target.checked)} />
                  </Cellule>
                  <Cellule fort>
                    {d.famille || d.responsable || "—"}
                    {d.app && <span className="ml-2 align-middle"><Badge ton="vert">a l'app</Badge></span>}
                    {avecLien && d.places > 0 && <span className="ml-2 align-middle"><Badge ton="neutre">{d.places} place{d.places > 1 ? "s" : ""}</Badge></span>}
                    {avecLien && d.lien_deja && <span className="ml-2 align-middle"><Badge ton="neutre">lien déjà reçu</Badge></span>}
                  </Cellule>
                  <Cellule muet>{(d.inscrits || []).join(", ") || "—"}</Cellule>
                  <Cellule muet><span className="break-all">{d.email}</span></Cellule>
                </>
              )}
            />
          </>
        )}
      </Section>

      <Section titre={<Etape n={3}>Le message</Etape>} testid="envois-message">
        <div className="bg-white rounded-2xl border p-5" style={{ borderColor: "var(--border)" }}>
          {modeles.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {modeles.map((m) => (
                <button key={m.cle} className="btn-ghost" data-testid="envois-modele"
                  onClick={() => modele(m)}>
                  <Mail size={13} /> {m.nom}
                </button>
              ))}
            </div>
          )}
          <label className="block text-xs font-semibold text-[var(--muted)] mb-1">Objet</label>
          <input className="input-field w-full" value={sujet} data-testid="envois-sujet"
            placeholder="Stage d'automne — infos pratiques"
            onChange={(e) => setSujet(e.target.value)} />
          <label className="block text-xs font-semibold text-[var(--muted)] mb-1 mt-4">Message</label>
          <textarea className="input-field w-full" rows={9} value={message} data-testid="envois-texte"
            placeholder={"Bonjour,\n\nLe stage commence lundi à 9h00. Merci de prévoir…"}
            onChange={(e) => setMessage(e.target.value)} />
          <p className="text-[11px] text-[var(--muted)] mt-2">
            « Bonjour Prénom » est ajouté automatiquement, chaque famille avec son prénom.
          </p>
          {/* Les repères de mise en page (21.10.2026) : au début d'un paragraphe, ils donnent
              au message le style des e-mails 1000 ARTS (voir envois._html). */}
          <details className="text-[11px] text-[var(--muted)] mt-1" data-testid="envois-mise-en-forme">
            <summary className="cursor-pointer select-none">Mise en forme (titres, étapes, bouton…)</summary>
            <div className="mt-1 leading-relaxed">
              Au début d'un paragraphe (une ligne vide entre deux paragraphes) :{" "}
              <code>## Titre</code> section · <code>### Titre</code> carte · <code>- élément</code> liste ·{" "}
              <code>1. étape</code> étapes numérotées · <code>&gt; texte</code> encadré turquoise ·{" "}
              <code>💡 astuce</code> · <code>[Libellé](https://…)</code> bouton · <code>**mot**</code> gras.
              Le modèle « Application 1000 ARTS : mode d'emploi » les utilise tous.
            </div>
          </details>
          <div className="flex flex-wrap items-center gap-3 mt-4 rounded-xl px-4 py-3" data-testid="envois-espace"
            style={{ background: "#F8FAFC", border: "1px solid var(--border)" }}>
            <span className="text-sm font-semibold">Visible dans l'app des familles</span>
            <Puces valeur={espace ? "oui" : "non"} onChange={(v) => setEspace(v === "oui")} testid="envois-espace-choix"
              options={[{ valeur: "oui", label: "Oui", testid: "envois-espace-oui" }, { valeur: "non", label: "Non", testid: "envois-espace-non" }]} />
            <span className="text-xs text-[var(--muted)] basis-full">
              {espace
                ? "L'e-mail part, et le message apparaît aussi dans l'app : sur l'Accueil tant qu'il n'est pas lu, puis dans Profil → Messages de l'école."
                : "E-mail seulement : rien n'apparaît dans l'app (par exemple l'invitation à installer l'app)."}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-4">
            <label className="btn-ghost cursor-pointer" data-testid="envois-piece-label">
              <Paperclip size={13} /> {piece ? `Pièce jointe : ${piece}` : "Pièce jointe (PDF, 8 Mo max)"}
              <input type="file" ref={fichier} className="hidden" data-testid="envois-piece"
                onChange={(e) => setPiece(e.target.files?.[0]?.name || "")} />
            </label>
            {piece && (
              <button className="text-xs underline text-[var(--muted)]" data-testid="envois-piece-retirer"
                onClick={() => { if (fichier.current) fichier.current.value = ""; setPiece(""); }}>retirer</button>
            )}
            <button className="btn-ghost" onClick={enregistrerModele}
              disabled={!sujet.trim()} data-testid="envois-enregistrer-modele">
              <Save size={13} /> Enregistrer comme modèle
            </button>
            <button className="btn-primary" onClick={envoyer} data-testid="envois-envoyer"
              disabled={envoi || !sujet.trim() || !message.trim() || retenus.length === 0}>
              <Send size={14} /> {envoi ? "Envoi…" : retenus.length === 1
                ? `Envoyer à ${retenus[0].famille || retenus[0].email}` : `Envoyer à ${famillesMot(retenus.length)}`}
            </button>
          </div>
        </div>
      </Section>

      {histo.length > 0 && (
        <Section titre="Derniers envois" testid="envois-historique">
          <Tableau
            testid="tableau-envois-historique"
            entetes={["Quand", "Objet", "Groupe", "Familles", "Dans l'app", "Pièce jointe"]}
            lignes={histo}
            vide="Aucun envoi."
            onClic={(h) => api(`/envois/${encodeURIComponent(h.id)}`).then(setVu)
              .catch((e) => informer({ titre: "Envoi introuvable", ton: "danger", texte: e.message }))}
            rendu={(h) => (
              <>
                <Cellule muet>{String(h.le || "").slice(0, 16).replace("T", " ")}</Cellule>
                <Cellule fort>{h.sujet}</Cellule>
                <Cellule muet>{h.groupe}</Cellule>
                <Cellule>{h.statut === "en_cours" ? `${h.nb || 0} / ${h.a_envoyer}` : h.nb}
                  {h.echecs ? ` · ${h.echecs} échec(s)` : ""}</Cellule>
                <Cellule muet>{h.retire_app_le ? "retiré de l'app"
                  : h.dans_app ? `lu par ${h.lus} sur ${h.dans_app}` : h.espace === false ? "e-mail seulement" : "—"}</Cellule>
                <Cellule muet>{h.piece || "—"}</Cellule>
              </>
            )}
          />
          <p className="text-xs text-[var(--muted)]">Clique sur un envoi pour relire le message et voir à qui il est parti.</p>
        </Section>
      )}

      {vu && (
        <ModaleEnvoi envoi={vu} onFermer={() => setVu(null)}
          onRetire={() => { api(`/envois/${encodeURIComponent(vu.id)}`).then(setVu).catch(() => setVu(null)); chargerModeles(); }}
          onReprendre={vu.message ? () => {
            setSujet(vu.sujet || ""); setMessage(vu.message || ""); setVu(null);
            window.scrollTo({ top: 0, behavior: "smooth" });
          } : null} />
      )}
    </div>
  );
}

const quand = (iso) => {
  const t = String(iso || "");
  return t ? `${t.slice(8, 10)}.${t.slice(5, 7)}.${t.slice(0, 4)} à ${t.slice(11, 16)}` : "";
};

const STATUTS = {
  envoye: { ton: "vert", mot: "envoyé", Icon: CheckCircle2 },
  echec: { ton: "rouge", mot: "échec", Icon: AlertTriangle },
  en_attente: { ton: "ambre", mot: "en attente", Icon: Clock },
};

/* Un envoi passé : le message exactement comme les familles l'ont reçu (le « Bonjour
   Prénom » s'ajoutait pour chacune), puis famille par famille, parti ou non. */
const APP = {
  lu: { ton: "vert", mot: "lu dans l'app" },
  pas_lu: { ton: "neutre", mot: "pas encore lu" },
  retire: { ton: "neutre", mot: "retiré de l'app" },
};

function ModaleEnvoi({ envoi, onFermer, onReprendre, onRetire }) {
  const { demander, informer } = useConfirmation();
  const etats = envoi.etats || [];
  const retirer = async () => {
    if (!await demander({
      titre: "Retirer ce message de l'app ?",
      texte: `« ${envoi.sujet} » disparaît de l'app des ${envoi.dans_app} famille(s) qui l'ont reçu.`,
      points: ["L'e-mail, lui, est déjà parti : on ne peut pas le rattraper."],
      valider: "Retirer de l'app", ton: "danger",
    })) return;
    try {
      const r = await api(`/envois/${encodeURIComponent(envoi.id)}/retirer-app`, { method: "POST" });
      await informer({ titre: "Retiré de l'app", ton: "succes", texte: `${r.retires} famille(s) ne le voient plus.` });
      onRetire?.();
    } catch (e) {
      await informer({ titre: "Impossible de retirer", ton: "danger", texte: String(e.message || e) });
    }
  };
  const echecs = etats.filter((e) => e.statut === "echec").length;
  return (
    <Modale large titre={envoi.sujet} testid="envoi-detail" onFermer={onFermer}
      sous_titre={[quand(envoi.le), envoi.groupe, envoi.par ? `par ${envoi.par}` : "",
        `${envoi.nb || 0} famille(s)${echecs ? ` · ${echecs} échec(s)` : ""}`].filter(Boolean).join(" · ")}>
      <div className="space-y-5">
        <div className="rounded-xl border p-4" style={{ borderColor: "var(--border)", background: "#FAFCFD" }}>
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)] mb-2">Message</div>
          <p className="text-sm text-[var(--muted)] mb-2">Bonjour Prénom,</p>
          {envoi.html
            ? <div className="text-sm leading-relaxed" data-testid="envoi-detail-message"
                dangerouslySetInnerHTML={{ __html: envoi.html }} />
            : <p className="text-sm text-[var(--muted)]">Le texte de cet envoi n'a pas été gardé.</p>}
          {envoi.piece && !envoi.html && (
            <p className="text-xs text-[var(--muted)] mt-2">Document joint : {envoi.piece}</p>
          )}
          {envoi.ancien_format && (
            <p className="text-xs text-[var(--muted)] mt-3">Envoi d'avant le suivi famille par famille : la console n'avait gardé que la liste des adresses servies, toutes ont reçu l'e-mail.</p>
          )}
          {envoi.retire_app_le ? (
            <p className="text-xs text-[var(--muted)] mt-3">Retiré de l'app le {quand(envoi.retire_app_le)} : les familles ne le voient plus.</p>
          ) : envoi.dans_app > 0 ? (
            <p className="text-xs text-[var(--muted)] mt-3" data-testid="envoi-detail-app">
              Dans l'app de {envoi.dans_app} famille(s) · lu par {envoi.lus}.
            </p>
          ) : envoi.espace === false ? (
            <p className="text-xs text-[var(--muted)] mt-3">E-mail seulement : pas dans l'app.</p>
          ) : null}
        </div>

        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)] mb-2">
            Destinataires ({etats.length})
          </div>
          <div className="rounded-xl border divide-y max-h-[320px] overflow-auto" style={{ borderColor: "var(--border)" }}
            data-testid="envoi-detail-destinataires">
            {etats.map((e) => {
              const st = STATUTS[e.statut] || STATUTS.en_attente;
              return (
                <div key={e.email} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm"
                  style={{ borderColor: "var(--border-soft)" }}>
                  <span className="font-medium">{e.responsable || e.email}</span>
                  {e.responsable && <span className="text-xs text-[var(--muted)] break-all">{e.email}</span>}
                  <span className="ml-auto flex gap-1.5">
                    {APP[e.app] && <Badge ton={APP[e.app].ton}>{APP[e.app].mot}</Badge>}
                    <Badge ton={st.ton}><st.Icon size={11} /> {st.mot}</Badge>
                  </span>
                  {e.erreur && <div className="w-full text-xs" style={{ color: "#B91C1C" }}>{e.erreur}</div>}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 justify-end">
          {envoi.dans_app > 0 && !envoi.retire_app_le && (
            <button className="btn-ghost mr-auto" onClick={retirer} data-testid="envoi-detail-retirer-app" style={{ color: "#B91C1C" }}>
              Retirer de l'app
            </button>
          )}
          {onReprendre && (
            <button className="btn-ghost" onClick={onReprendre} data-testid="envoi-detail-reprendre">
              Reprendre ce texte pour un nouvel envoi
            </button>
          )}
          <button className="btn-ghost" onClick={onFermer}>Fermer</button>
        </div>
      </div>
    </Modale>
  );
}
